import React, { useEffect, useState } from "react";
import PrescriptionImage from "../PrescriptionImage";
import DeliveryInfo from "../DeliveryInfo";
import { Order } from "../types";
import WithoutPrescriptionMedicineOrderDetails from "./WithoutPrescriptionMedicineOrderDetails";
import { OrderDetailsSkeleton } from "../../StockOrderSkeleton";
import { AdminOrderService } from "../../../../../../services/api/AdminOrderService/admin.order.service";
import toast from "react-hot-toast";

interface Props {
  onPrepareOrder: () => void;
  showWithoutPrescriptionOrderDetails: boolean;
  setShowGeneratePrescriptionForm: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  selectedOrder: Order | null;
  isLoading?: boolean;
  onOrderCancelled?: (orderId: string) => void;
  fetchOrder: () => void;
}

const MedicineOrderDetails: React.FC<Props> = ({
  onPrepareOrder,
  showWithoutPrescriptionOrderDetails,
  setShowGeneratePrescriptionForm,
  selectedOrder,
  isLoading = false,
  onOrderCancelled,
  fetchOrder,
}) => {
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(true);

  const fetchOrderDetails = async () => {
    if (selectedOrder?.id) {
      try {
        setLoadingDetails(true);
        const response = await AdminOrderService.getAdminOrderDetails(
          selectedOrder.id
        );
        setOrderDetails(response.data);
      } catch (error) {
        console.error("Failed to fetch order details:", error);
        toast.error("Failed to load order details");
      } finally {
        setLoadingDetails(false);
      }
    }
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [selectedOrder?.id]);


  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const isPrepareOrderDisabled = orderDetails?.orderStatus !== "PENDING_ADMIN";
  const isOrderCancelled = orderDetails?.orderStatus === "CANCELLED";

  const handleCancelOrder = async () => {
    try {
      const response = await AdminOrderService.cancelOrder(
        selectedOrder?.id.toString() || ""
      );

      if (response.success) {
        toast.success(response.message);
        onOrderCancelled?.(selectedOrder?.id.toString() || "");
        fetchOrderDetails();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to cancel order.");
    }
  };

  if (isLoading || loadingDetails) {
    return <OrderDetailsSkeleton />;
  }

  if (!orderDetails) {
    return (
      <div className="p-6 border border-dashed border-cyan-300 rounded-lg text-center text-gray-500 flex flex-col items-center gap-2 w-full min-h-screen">
        <p className="text-lg font-medium">No order details found</p>
      </div>
    );
  }


  return (
    <>
      {showWithoutPrescriptionOrderDetails ? (
        <WithoutPrescriptionMedicineOrderDetails
          setShowGeneratePrescriptionForm={setShowGeneratePrescriptionForm}
          selectedOrder={orderDetails}
          orderDetails={fetchOrderDetails}
        />
      ) : (
        <div
          className="bg-white border border-gray-200 p-6 h-[calc(100vh-64px)] overflow-y-auto"
          style={{
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
          }}
        >
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              Order ID: {orderDetails?.orderNumber || orderDetails?.id}
            </p>
            <h2 className="text-lg font-semibold text-gray-900">
              Order Details
            </h2>
          </div>

          {orderDetails?.hasPrescription && (
            <PrescriptionImage
              imageUrls={
                orderDetails?.prescriptionDetails?.map(
                  (p: any) => p.prescriptionImage
                ) || []
              }
            />
          )}

          <div className="space-y-4 mb-6">
            {orderDetails?.productOrderItems?.map(
              (item: any, index: number) => (
                <div key={index} className="flex items-center gap-4">
                  {item.productImage && (
                    <img
                      src={item.productImage}
                      alt={item.productName}
                      width={50}
                      height={50}
                      className="rounded border"
                    />
                  )}
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-gray-800">
                      {item.productName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {item.productDescription}
                    </p>
                    <p className="text-xs text-gray-500">
                      MRP: ₹{item.productPrice?.product_mrp} | Price: ₹
                      {item.productPrice?.final_selling_price} |
                      {item.productPrice?.selling_discount}% off
                    </p>
                  </div>
                  <div className="bg-gray-100 px-3 py-1 rounded-full text-sm font-medium">
                    {item.productsItemsCount}
                  </div>
                </div>
              )
            )}
          </div>

          <DeliveryInfo
            address={orderDetails?.orderAddress}
            pincode={orderDetails?.orderPincode}
            orderDate={
              orderDetails?.orderDate && formatDate(orderDetails.orderDate)
            }
            orderStatus={orderDetails?.orderStatus}
            patientId={orderDetails?.patientId}
            clinicDetails={orderDetails?.clinicDetails}
            patientDetails={orderDetails?.patientDetails}
            deliveryPersonDetails={orderDetails?.deliveryPersonDetails}
            paymentDetails={orderDetails?.paymentDetails}
          />

          <div className="flex gap-4">
            {!isOrderCancelled && (
              <button
                onClick={onPrepareOrder}
                disabled={isPrepareOrderDisabled}
                className={`px-4 py-2 rounded-md ${
                  isPrepareOrderDisabled
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700 text-white"
                }`}
              >
                {orderDetails?.orderStatus === "PENDING_ADMIN"
                  ? "Prepare Order"
                  : orderDetails?.orderStatus === "PENDING_PATIENT"
                  ? "Waiting Patient Confirmation"
                  : orderDetails?.orderStatus === "CLINIC_ASSIGNED"
                  ? "At Assigned Clinic"
                  : orderDetails?.orderStatus === "DELIVERY_ASSIGNED"
                  ? "Delivery Partner Assigned"
                  : orderDetails?.orderStatus === "OUT_FOR_DELIVERY"
                  ? "Out For Delivery"
                  : orderDetails?.orderStatus === "COMPLETED"
                  ? "Order Completed"
                  : "Prepare Order"}
              </button>
            )}
            {(orderDetails?.orderStatus === "PENDING_ADMIN" ||
              orderDetails?.orderStatus === "PENDING_PATIENT") &&
              !isOrderCancelled && (
                <button
                  onClick={handleCancelOrder}
                  className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white"
                >
                  Cancel Order
                </button>
              )}
            {orderDetails?.orderStatus === "CANCELLED" && (

              <button
                disabled
                className="px-4 py-2 rounded-md bg-gray-300 cursor-not-allowed"
              >
                Order Cancelled
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MedicineOrderDetails;
